import { Button } from '@/components/ui/button';
import Icon from '@/icons/icon';
import { cn } from '@/lib/utils';
import type { CSSProperties, ReactNode, Ref } from 'react';

interface BottomSheetProps {
    title: string;
    onClose: () => void;
    /** Stacking/size classes for the sheet itself (z-index, height, animation). */
    className?: string;
    style?: CSSProperties;
    /** Stacking classes for the dimmed backdrop. */
    backdropClassName?: string;
    titleClassName?: string;
    sheetRef?: Ref<HTMLDivElement>;
    children: ReactNode;
}

/** Mobile bottom sheet: backdrop, drag handle, title with close button, body. */
export default function BottomSheet({
    title,
    onClose,
    className,
    style,
    backdropClassName,
    titleClassName,
    sheetRef,
    children,
}: BottomSheetProps) {
    return (
        <>
            <div
                className={cn('fixed inset-0 bg-black/40', backdropClassName)}
                onClick={onClose}
            />
            <div
                ref={sheetRef}
                className={cn(
                    'fixed inset-x-0 bottom-0 flex flex-col rounded-t-2xl bg-card shadow-2xl',
                    className,
                )}
                style={style}
            >
                <div className="flex shrink-0 justify-center pt-3 pb-1">
                    <div className="h-1 w-10 rounded-full bg-gray-300" />
                </div>
                <div className="flex shrink-0 items-center justify-between px-4 pt-1 pb-3">
                    <span
                        className={cn(
                            'text-base font-bold text-gray-900',
                            titleClassName,
                        )}
                    >
                        {title}
                    </span>
                    <Button
                        variant="bare"
                        type="button"
                        aria-label="Close"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
                    >
                        <Icon
                            iconName="X"
                            className="h-5 w-5 stroke-gray-500"
                        />
                    </Button>
                </div>
                {children}
            </div>
        </>
    );
}
